<!-- @component Renders a hierarchical list of heading links in the document. -->
<script lang="ts">
  import { pushState } from "$app/navigation";
  import { page } from "$app/stores";

  import type { Toc } from "@stefanprobst/remark-extract-toc";
  import { onMount } from "svelte";

  import CrossLinksBox from "./CrossLinksBox.svelte";
  import TryThisFooter from "./TryThisFooter.svelte";

  export let toc: Toc;
  $: flatToc = flattenToc(toc);

  export let activeHeadings = new Set<string>();
  export let cliArgs: string[] | undefined;
  export let crossLinks: { text: string; href: string }[] | undefined =
    undefined;
  export let crossLinksText: string | undefined = undefined;

  /** Flattens all the child nestings in a tree table of contents. */
  function flattenToc(toc: Toc): Toc {
    const headings: Toc = [];
    for (const entry of toc) {
      headings.push({ value: entry.value, depth: entry.depth, id: entry.id });
      if (entry.children) {
        headings.push(...flattenToc(entry.children));
      }
    }
    return headings;
  }

  let tocElement: HTMLDivElement;
  let firstActiveId: string | undefined = undefined;

  $: if (activeHeadings.size)
    firstActiveId = flatToc.find(
      (entry) => entry.id && activeHeadings.has(entry.id),
    )?.id;

  // Catches scroll events outside of the ToC, and maintains correct focus of ToC.
  $: onMount(() => {
    const handleWheel = () => {
      if (firstActiveId) {
        const node = document.getElementById("toc-" + firstActiveId);
        if (node) {
          const target = node.offsetTop - tocElement.clientHeight / 2;
          tocElement.scrollTo({ top: target, behavior: "smooth" });
        }
      }
    };
    const stopWheel = (event: WheelEvent) => {
      event.stopPropagation();
    };
    document.addEventListener("wheel", handleWheel);
    tocElement.addEventListener("wheel", stopWheel);
    return () => {
      document.removeEventListener("wheel", handleWheel);
      tocElement.removeEventListener("wheel", stopWheel);
    };
  });
</script>

<!-- right side TOC -->
<div class="hidden lg:block text-c-gray-80 pl-6">
  <!-- TODO(gongy): figure out how to un-hard-code this -->
  <div class="sticky top-[98px] flex flex-col pb-10">
    <div
      class="flex flex-col max-h-[calc(100vh-166px)] scroller"
      bind:this={tocElement}
    >
      {#each flatToc as entry}
        {#if entry.id}
          <a
            id={"toc-" + entry.id}
            href={`#${entry.id}`}
            class="text-ellipsis overflow-x-hidden shrink-0"
            class:strong={firstActiveId == entry.id}
            class:active={activeHeadings.has(entry.id)}
            style:padding-left="{entry.depth * 16}px"
            on:click={(event) => {
              if (entry.id) {
                const node = document.getElementById(entry.id);
                if (node) {
                  event.preventDefault();
                  node.scrollIntoView({ behavior: "smooth" });
                  const url = new URL($page.url);
                  url.hash = `#${entry.id}`;
                  pushState(url, {});
                }
              }
            }}
          >
            {entry.value}
          </a>
        {/if}
      {/each}
    </div>
    {#if crossLinks}
      <div class="mt-6">
        <CrossLinksBox {crossLinks} {crossLinksText} />
      </div>
    {/if}
    {#if cliArgs}
      <div class="mt-16">
        <TryThisFooter {cliArgs} />
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  a {
    @apply py-2 border-l border-c-gray-10 text-sm text-c-gray-60 pr-2;
  }

  a.strong {
    @apply border-c-green-100 border-l-2 text-white bg-white/5 -ml-[1px];
  }

  a.active {
    @apply text-c-gray-90 bg-white/5;
  }

  a:hover {
    @apply text-c-gray-90;
  }

  .scroller {
    overflow-y: auto;
    overflow-y: overlay;
    overflow-x: hidden;
    scrollbar-color: transparent transparent; /* Firefox only */
  }

  .scroller:hover {
    scrollbar-color: rgb(90, 90, 90) rgba(0, 0, 0, 0); /* Firefox only */
  }

  .scroller::-webkit-scrollbar {
    width: 8px;
  }

  .scroller::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 10px;
  }

  .scroller:hover::-webkit-scrollbar-thumb {
    background: rgb(90, 90, 90);
  }
</style>
